import * as React from "react"
import { cn } from "../../utils/utils"

interface TooltipProps {
  children: React.ReactNode
}

interface TooltipTriggerProps {
  children: React.ReactNode
}

interface TooltipContentProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string
  children: React.ReactNode
}

const TooltipContext = React.createContext<{
  isVisible: boolean
  setIsVisible: (visible: boolean) => void
}>({
  isVisible: false,
  setIsVisible: () => {}
})

function Tooltip({ children }: TooltipProps) {
  const [isVisible, setIsVisible] = React.useState(false)
  
  return (
    <TooltipContext.Provider value={{ isVisible, setIsVisible }}>
      <div className="relative inline-block">
        {children}
      </div>
    </TooltipContext.Provider>
  )
}

function TooltipTrigger({ children }: TooltipTriggerProps) {
  const { setIsVisible } = React.useContext(TooltipContext)
  
  return (
    <div
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      className="cursor-pointer"
    >
      {children}
    </div>
  )
}

function TooltipContent({ className, children, ...props }: TooltipContentProps) {
  const { isVisible } = React.useContext(TooltipContext)
  
  if (!isVisible) return null
  
  return (
    <div
      className={cn(
        "absolute z-200 min-w-[200px] px-3 py-1.5 text-sm text-black bg-gray-900 rounded-md shadow-md -top-2 left-1/2 transform -translate-x-1/2 -translate-y-full",
        "before:content-[''] before:absolute before:top-full before:left-1/2 before:transform before:-translate-x-1/2",
        "before:border-4 before:border-transparent before:border-t-gray-900",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
}